import { splitVoices, type BoardPost } from '@talkofthetown/shared';
import { useMemo, useState } from 'react';
import type { BoardSnapshot } from '../lib/motion';
import type { Board } from './useBoard';

interface Snapshot extends BoardSnapshot {
  view: string;
  ranked: BoardPost[];
  latest: BoardPost[];
  /** Paid posts at the time (they're never moved up to fill the middle). */
  paid: ReadonlySet<string>;
}

export interface FrozenBoard {
  /** What the board shows: the snapshot from the last (re)load, composed to fit. */
  posts: BoardPost[];
  /** Voices that didn't fit the board, most opened first (they whisper around it). */
  hidden: BoardPost[];
  /** What the board would show now (within the chosen distance) differs from what it shows. */
  changed: boolean;
  refresh: () => void;
  /** The snapshot shown (the board animates when a new one arrives). */
  snapshot: BoardSnapshot;
  /** New and faded posts, newest first (live): heard when the reader listens to the whispers. */
  whispers: BoardPost[];
  /** Paid posts reaching this reader (live): whispered more, marked Sponsored. */
  sponsored: BoardPost[];
}

/**
 * What a reader would notice: which posts, in what order, new or ranked, and roughly how big. Sizes
 * are relative to the biggest (in 5% steps), so the clock fading every score alike changes nothing.
 */
function signature(posts: readonly BoardPost[]): string {
  const top = Math.max(0, ...posts.map((p) => p.score));
  return posts.map((p) => `${p.id}:${top ? Math.round((p.score / top) * 20) : 0}`).join(',');
}

/**
 * The board (the voices) never rearranges itself under the reader. It shows a snapshot, taken when a view
 * loads (first visit, another distance or place) or when the reader presses Refresh. Live changes
 * in between only raise `changed`. `view` identifies the view; `fit` is how many tiles fit.
 */
export function useFrozenBoard(live: Board, view: string, fit: number): FrozenBoard {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const take = (refreshed: boolean) =>
    setSnap((s) => ({
      view,
      ranked: live.posts,
      latest: live.latest,
      paid: new Set(live.sponsored.map((p) => p.id)),
      id: (s?.id ?? 0) + 1,
      refreshed,
    }));
  if (live.loaded && snap?.view !== view) take(false);
  // The board holds the voices; whispers are heard live, never frozen.
  const voices = useMemo(
    () => (snap ? splitVoices(snap.ranked, snap.latest, snap.paid).voices : []),
    [snap],
  );
  const shown = useMemo(() => voices.slice(0, fit), [voices, fit]);
  const hidden = useMemo(() => voices.slice(fit), [voices, fit]);
  const livePaid = useMemo(() => new Set(live.sponsored.map((p) => p.id)), [live.sponsored]);
  const heard = useMemo(
    () => splitVoices(live.posts, live.latest, livePaid),
    [live.posts, live.latest, livePaid],
  );
  const current = live.loaded ? heard.voices.slice(0, fit) : shown;
  // Paid posts whisper (live, like the whispers) unless they're already a voice; a paid post that is
  // also an ordinary whisper is whispered once, as sponsored.
  const sponsored = useMemo(() => {
    const voiced = new Set(shown.map((p) => p.id));
    return live.sponsored.filter((p) => !voiced.has(p.id));
  }, [live.sponsored, shown]);
  const paid = useMemo(() => new Set(sponsored.map((p) => p.id)), [sponsored]);
  const whispers = useMemo(
    () => heard.whispers.filter((p) => !paid.has(p.id)),
    [heard.whispers, paid],
  );
  return {
    posts: shown,
    hidden,
    whispers,
    sponsored,
    changed: snap?.view === view && live.loaded && signature(current) !== signature(shown),
    refresh: () => take(true),
    snapshot: { id: snap?.id ?? 0, refreshed: snap?.refreshed ?? false },
  };
}
